import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { useToast } from '../ui/use-toast';
import { supabase } from '../../integrations/supabase/client';
import { useAuth } from '../../hooks/useAuth';
import { ensureDir } from '../../utils/filesystem';
import { globalVFS } from '../../utils/storage/VFS';
import {
  MSeedBehavior,
  MSeedBlock,
  MSeedHeader,
  MSeedJsonBlock,
  MSeedPayload,
  ParsedMSeed,
  buildSeedPayload,
  bytesToBase64,
  compileWatToWasmBytes,
  createPasswordProtection,
  createSeedBinary,
  decodeStoredBinaryContent,
  downloadBinaryFile,
  encryptPayloadForHeader,
  instantiateWasmBehavior,
  makeBehaviorBlock,
  makeImageBlock,
  makeJsonBlock,
  makeTextBlock,
  makeVectorBlock,
  parseSeedBinary,
  seedHasPasswordProtection,
  slugifySeedTitle,
  summarizeBlueprintDraft,
  verifyProof,
} from '../../lib/mseed';
import {
  Archive,
  BadgeCheck,
  Binary,
  BookOpen,
  Copy,
  Download,
  Eye,
  EyeOff,
  FileArchive,
  Image as ImageIcon,
  KeyRound,
  Loader2,
  Lock,
  Network,
  PackagePlus,
  Rocket,
  Save,
  ShieldCheck,
  Sparkles,
  Unlock,
  Upload,
  Waypoints,
} from 'lucide-react';

interface MapleSeedVaultProps {
  filePath?: string;
  fileContent?: string;
}

type SeedProtectionMode = 'standard' | 'password';

type KeyAuthorityPublicKeyResponse = {
  publicJwk: JsonWebKey;
  keyId: string;
};

type IssueSealResponse = {
  seal: MSeedHeader['seal'];
};

type UnlockSeedResponse = {
  payload: MSeedPayload;
  sealValid: boolean;
  authorityVerified: boolean;
  passwordVerified: boolean;
};

type GeneratedAuthoritySecrets = {
  publicJwk: string;
  privateJwk: string;
  sealSecret: string;
};

const BLUEPRINT_STORAGE_KEY = 'mapleos_blueprint_studio';
const DEFAULT_SAVE_USER = 'User';
const UNLOCK_FAILURE_MESSAGE = 'Unable to unlock seed. Check the password or file integrity.';
const SAMPLE_WAT = `(module
  (func (export "run") (result i32)
    i32.const 42)
  (func (export "add") (param i32 i32) (result i32)
    local.get 0
    local.get 1
    i32.add))`;

const MapleSeedVault: React.FC<MapleSeedVaultProps> = ({ filePath, fileContent }) => {
  const { toast } = useToast();
  const { user, profile } = useAuth();
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);

  const [activeTab, setActiveTab] = useState<'create' | 'dossier'>('create');
  const [title, setTitle] = useState('Maple Seed');
  const [summary, setSummary] = useState('A protected MapleOS dossier containing data, behavior, and proof.');
  const [author, setAuthor] = useState(profile?.username || user?.email || 'MapleOS User');
  const [tags, setTags] = useState('dossier, mapleos, seed');
  const [runbookText, setRunbookText] = useState('## Mission\nDocument what this seed contains and why it matters.');
  const [vectorJson, setVectorJson] = useState('');
  const [watSource, setWatSource] = useState(SAMPLE_WAT);
  const [imageDataUrl, setImageDataUrl] = useState<string>('');
  const [imageName, setImageName] = useState('dossier-image');
  const [blueprintDraft, setBlueprintDraft] = useState<any | null>(null);
  const [blueprintSummary, setBlueprintSummary] = useState<any | null>(null);
  const [protectionMode, setProtectionMode] = useState<SeedProtectionMode>('standard');
  const [buildPassword, setBuildPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showBuildPassword, setShowBuildPassword] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState('');
  const [showUnlockPassword, setShowUnlockPassword] = useState(false);
  const [building, setBuilding] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [generatingSecrets, setGeneratingSecrets] = useState(false);
  const [authoritySecrets, setAuthoritySecrets] = useState<GeneratedAuthoritySecrets | null>(null);
  const [builtBytes, setBuiltBytes] = useState<Uint8Array | null>(null);
  const [builtHeader, setBuiltHeader] = useState<MSeedHeader | null>(null);
  const [savePath, setSavePath] = useState('');
  const [openedSeed, setOpenedSeed] = useState<ParsedMSeed | null>(null);
  const [payload, setPayload] = useState<MSeedPayload | null>(null);
  const [sealVerified, setSealVerified] = useState(false);
  const [authorityVerified, setAuthorityVerified] = useState(false);
  const [proofState, setProofState] = useState<{ valid: boolean; errors: string[] } | null>(null);
  const [behaviorRuntime, setBehaviorRuntime] = useState<{ exports: string[]; result?: string; logs: string[] } | null>(null);
  const [unlockErrorMessage, setUnlockErrorMessage] = useState('');

  const osUsername = useMemo(() => {
    const candidate = profile?.username || user?.email?.split('@')[0] || DEFAULT_SAVE_USER;
    return candidate.replace(/[^a-zA-Z0-9]/g, '') || DEFAULT_SAVE_USER;
  }, [profile?.username, user?.email]);

  const defaultSaveRoot = useMemo(() => `C:\\User\\${osUsername}\\Documents\\Seeds`, [osUsername]);

  useEffect(() => {
    setAuthor(profile?.username || user?.email || 'MapleOS User');
  }, [profile?.username, user?.email]);

  useEffect(() => {
    if (!title.trim()) {
      return;
    }
    setSavePath(`${defaultSaveRoot}\\${slugifySeedTitle(title)}.mseed`);
  }, [defaultSaveRoot, title]);

  const resetUnlockedState = () => {
    setPayload(null);
    setSealVerified(false);
    setAuthorityVerified(false);
    setProofState(null);
    setBehaviorRuntime(null);
    setUnlockPassword('');
    setUnlockErrorMessage('');
  };

  useEffect(() => {
    if (!fileContent) {
      return;
    }

    try {
      const parsed = parseSeedBinary(decodeStoredBinaryContent(fileContent));
      setOpenedSeed(parsed);
      resetUnlockedState();
      setActiveTab('dossier');
    } catch (error: any) {
      toast({
        title: 'Unable to open seed',
        description: error?.message || 'This file is not a valid .mseed container.',
        variant: 'destructive',
      });
    }
  }, [fileContent, toast]);

  const dossierHeader = openedSeed?.header || builtHeader;
  const textBlocks = payload?.blocks.filter((block): block is Extract<MSeedBlock, { kind: 'text' }> => block.kind === 'text') || [];
  const imageBlocks = payload?.blocks.filter((block): block is Extract<MSeedBlock, { kind: 'image' }> => block.kind === 'image') || [];
  const vectorBlocks = payload?.blocks.filter((block): block is Extract<MSeedBlock, { kind: 'vector' }> => block.kind === 'vector') || [];
  const jsonBlocks = payload?.blocks.filter((block): block is MSeedJsonBlock => block.kind === 'json' || block.kind === 'blueprint') || [];

  const parsedTags = useMemo(
    () => tags.split(',').map((item) => item.trim()).filter(Boolean),
    [tags]
  );

  const hasAnyData = useMemo(
    () => Boolean(runbookText.trim() || vectorJson.trim() || imageDataUrl || blueprintDraft),
    [runbookText, vectorJson, imageDataUrl, blueprintDraft]
  );

  const passwordStrength = useMemo(() => {
    const checks = [
      buildPassword.length >= 12,
      /[a-z]/.test(buildPassword) && /[A-Z]/.test(buildPassword),
      /\d/.test(buildPassword),
      /[^A-Za-z0-9]/.test(buildPassword),
    ];
    const score = checks.filter(Boolean).length;

    if (!buildPassword) {
      return {
        score,
        label: 'Use 12+ characters with mixed case, numbers, and symbols.',
        tone: 'text-muted-foreground',
      };
    }

    if (score <= 1) {
      return {
        score,
        label: 'Weak password. Add length, numbers, and symbols to resist brute-force attacks.',
        tone: 'text-destructive',
      };
    }

    if (score <= 3) {
      return {
        score,
        label: 'Reasonable protection. A longer, unique passphrase would be stronger.',
        tone: 'text-amber-600 dark:text-amber-400',
      };
    }

    return {
      score,
      label: 'Strong password guidance met. Share it separately from the .mseed file.',
      tone: 'text-green-600 dark:text-green-400',
    };
  }, [buildPassword]);

  const isPasswordBuild = protectionMode === 'password';
  const isPasswordProtectedSeed = seedHasPasswordProtection(dossierHeader);

  const invokeKeyAuthority = async <T,>(body: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke('mapleos-key-authority', { body });
    if (error) {
      throw new Error(error.message || 'MapleOS Key Authority request failed.');
    }
    return data as T;
  };

  const generateSealSecret = () => {
    const bytes = crypto.getRandomValues(new Uint8Array(64));
    return Array.from(bytes).map((byte) => byte.toString(16).padStart(2, '0')).join('');
  };

  const handleGenerateAuthoritySecrets = async () => {
    setGeneratingSecrets(true);
    try {
      const keyPair = await crypto.subtle.generateKey(
        {
          name: 'RSA-OAEP',
          modulusLength: 3072,
          publicExponent: new Uint8Array([1, 0, 1]),
          hash: 'SHA-256',
        },
        true,
        ['encrypt', 'decrypt']
      );

      const exportedPublicJwk = await crypto.subtle.exportKey('jwk', keyPair.publicKey);
      const exportedPrivateJwk = await crypto.subtle.exportKey('jwk', keyPair.privateKey);

      const normalizedPublicJwk: JsonWebKey = {
        ...exportedPublicJwk,
        alg: 'RSA-OAEP-256',
        key_ops: ['encrypt'],
        ext: true,
      };

      const normalizedPrivateJwk: JsonWebKey = {
        ...exportedPrivateJwk,
        alg: 'RSA-OAEP-256',
        key_ops: ['decrypt'],
        ext: true,
      };

      setAuthoritySecrets({
        publicJwk: JSON.stringify(normalizedPublicJwk, null, 2),
        privateJwk: JSON.stringify(normalizedPrivateJwk, null, 2),
        sealSecret: generateSealSecret(),
      });

      toast({
        title: 'Authority secrets generated',
        description: 'Copy these values into your Supabase Edge Function secrets.',
      });
    } catch (error: any) {
      toast({
        title: 'Generation failed',
        description: error?.message || 'The browser could not generate a MapleOS authority keypair.',
        variant: 'destructive',
      });
    } finally {
      setGeneratingSecrets(false);
    }
  };

  const handleCopySecret = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: `${label} copied`, description: 'Paste it into Supabase secrets.' });
    } catch {
      toast({ title: 'Copy failed', description: `Select and copy the ${label} manually.`, variant: 'destructive' });
    }
  };

  const loadLatestBlueprintDraft = () => {
    try {
      const raw = localStorage.getItem(BLUEPRINT_STORAGE_KEY);
      if (!raw) {
        toast({
          title: 'No Blueprint Studio draft found',
          description: 'Create or save a blueprint first, then import it into a seed.',
          variant: 'destructive',
        });
        return;
      }

      const parsed = JSON.parse(raw);
      setBlueprintDraft(parsed);
      setBlueprintSummary(summarizeBlueprintDraft(parsed));
      toast({ title: 'Blueprint imported', description: 'The latest Blueprint Studio draft will be bundled into this seed.' });
    } catch {
      toast({ title: 'Blueprint import failed', description: 'The latest blueprint draft could not be parsed.', variant: 'destructive' });
    }
  };

  const onImportSeedClick = () => {
    importInputRef.current?.click();
  };

  const onImageUploadClick = () => {
    imageInputRef.current?.click();
  };

  const handleImportSeedFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const bytes = new Uint8Array(await file.arrayBuffer());
    try {
      const parsed = parseSeedBinary(bytes);
      setOpenedSeed(parsed);
      resetUnlockedState();
      setActiveTab('dossier');
      toast({ title: 'Seed imported', description: `${file.name} is ready to unlock inside MapleOS.` });
    } catch (error: any) {
      toast({ title: 'Import failed', description: error?.message || 'This file is not a valid .mseed seed.', variant: 'destructive' });
    } finally {
      event.target.value = '';
    }
  };

  const handleImageSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImageDataUrl(String(reader.result || ''));
      setImageName(file.name.replace(/\.[^.]+$/, '') || 'dossier-image');
      toast({ title: 'Image added', description: `${file.name} will be packaged into the seed.` });
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const buildBlocks = async (): Promise<{ blocks: MSeedBlock[]; behavior: MSeedBehavior | null; source: 'manual' | 'blueprint' | 'hybrid'; }> => {
    const blocks: MSeedBlock[] = [];

    if (summary.trim()) {
      blocks.push(await makeTextBlock('overview.md', `# ${title.trim()}\n\n${summary.trim()}`));
    }

    if (runbookText.trim()) {
      blocks.push(await makeTextBlock('runbook.md', runbookText.trim()));
    }

    if (imageDataUrl) {
      blocks.push(await makeImageBlock(`${imageName}.image`, imageDataUrl));
    }

    if (vectorJson.trim()) {
      const parsed = JSON.parse(vectorJson);
      if (!Array.isArray(parsed) || parsed.some((value) => typeof value !== 'number')) {
        throw new Error('Vector JSON must be an array of numbers.');
      }
      blocks.push(await makeVectorBlock('vector.json', parsed));
    }

    if (blueprintDraft) {
      blocks.push(await makeJsonBlock('blueprint.json', blueprintDraft, 'blueprint'));
      blocks.push(await makeTextBlock(
        'blueprint-summary.md',
        `# Blueprint Summary\n\n- Name: ${blueprintSummary?.name || 'Blueprint Draft'}\n- Nodes: ${blueprintSummary?.nodeCount || 0}\n- Edges: ${blueprintSummary?.edgeCount || 0}\n- Node Types: ${(blueprintSummary?.nodeTypes || []).join(', ') || 'n/a'}`
      ));
    }

    let behavior: MSeedBehavior | null = null;
    if (watSource.trim()) {
      const wasmBytes = await compileWatToWasmBytes(watSource.trim());
      behavior = await makeBehaviorBlock('behavior.wasm', wasmBytes, 'wat', watSource.trim());
    }

    const source = blueprintDraft && hasAnyData ? 'hybrid' : blueprintDraft ? 'blueprint' : 'manual';

    return { blocks, behavior, source };
  };

  const handleBuildSeed = async () => {
    if (!title.trim()) {
      toast({ title: 'Title required', description: 'Give this seed a title before building it.', variant: 'destructive' });
      return;
    }

    if (!summary.trim()) {
      toast({ title: 'Summary required', description: 'Give this seed a short summary so the dossier can explain itself.', variant: 'destructive' });
      return;
    }

    if (isPasswordBuild) {
      if (buildPassword.length < 10) {
        toast({
          title: 'Password too short',
          description: 'Use at least 10 characters for password-protected authority seeds.',
          variant: 'destructive',
        });
        return;
      }

      if (buildPassword !== confirmPassword) {
        toast({
          title: 'Passwords do not match',
          description: 'Confirm the same password before building the seed.',
          variant: 'destructive',
        });
        return;
      }
    }

    setBuilding(true);
    try {
      const { blocks, behavior, source } = await buildBlocks();
      if (blocks.length === 0 && !behavior) {
        throw new Error('Add at least one data block or a behavior block before packaging a seed.');
      }

      const payloadDraft = await buildSeedPayload({
        title,
        summary,
        author,
        tags: parsedTags,
        source,
        blocks,
        behavior,
      });

      const { publicJwk, keyId } = await invokeKeyAuthority<KeyAuthorityPublicKeyResponse>({ action: 'public_key' });
      const { seal } = await invokeKeyAuthority<IssueSealResponse>({
        action: 'issue_seal',
        seedId: payloadDraft.manifest.seedId,
        manifestHash: payloadDraft.proof.manifestHash,
        createdAt: payloadDraft.manifest.createdAt,
      });

      const passwordProtection = isPasswordBuild ? await createPasswordProtection(buildPassword) : undefined;
      const { header, ciphertext } = await encryptPayloadForHeader(payloadDraft, publicJwk, keyId, seal, passwordProtection);
      const bytes = createSeedBinary(header, ciphertext);
      const normalizedPath = savePath.replace(/\//g, '\\');
      const directory = normalizedPath.slice(0, normalizedPath.lastIndexOf('\\'));
      await ensureDir(directory);
      await globalVFS.save(normalizedPath, bytesToBase64(bytes));

      setBuiltBytes(bytes);
      setBuiltHeader(header);
      setOpenedSeed(parseSeedBinary(bytes));
      resetUnlockedState();
      setActiveTab('dossier');

      toast({
        title: '.mseed built',
        description: `${title.trim()} was built and saved to ${normalizedPath}.`,
      });
    } catch (error: any) {
      toast({ title: 'Build failed', description: error?.message || 'MapleOS could not build this seed.', variant: 'destructive' });
    } finally {
      setBuilding(false);
    }
  };

  const handleSaveToMapleOS = async () => {
    if (!builtBytes) {
      toast({ title: 'Nothing to save', description: 'Build a seed first.', variant: 'destructive' });
      return;
    }

    try {
      const normalizedPath = savePath.replace(/\//g, '\\');
      const directory = normalizedPath.slice(0, normalizedPath.lastIndexOf('\\'));
      await ensureDir(directory);
      await globalVFS.save(normalizedPath, bytesToBase64(builtBytes));
      toast({ title: 'Saved into MapleOS', description: `${normalizedPath} is now available in File Explorer.` });
    } catch (error: any) {
      toast({ title: 'Save failed', description: error?.message || 'MapleOS could not save this seed into the VFS.', variant: 'destructive' });
    }
  };

  const handleDownloadSeed = () => {
    if (!builtBytes) {
      toast({ title: 'Nothing to download', description: 'Build a seed first.', variant: 'destructive' });
      return;
    }

    downloadBinaryFile(builtBytes, `${slugifySeedTitle(title)}.mseed`);
    toast({ title: 'Download started', description: 'The .mseed file is being downloaded to your machine.' });
  };

  const handleUnlockSeed = async () => {
    if (!openedSeed) {
      toast({ title: 'No seed loaded', description: 'Open or build a seed first.', variant: 'destructive' });
      return;
    }

    if (seedHasPasswordProtection(openedSeed.header) && !unlockPassword.trim()) {
      setUnlockErrorMessage(UNLOCK_FAILURE_MESSAGE);
      toast({ title: 'Password required', description: 'Enter the password for this protected seed.', variant: 'destructive' });
      return;
    }

    setUnlocking(true);
    setUnlockErrorMessage('');
    try {
      const { payload: decryptedPayload, sealValid, authorityVerified: authorityOk } = await invokeKeyAuthority<UnlockSeedResponse>({
        action: 'unlock_seed',
        header: openedSeed.header,
        ciphertextBase64: bytesToBase64(openedSeed.ciphertext),
        ...(seedHasPasswordProtection(openedSeed.header) ? { password: unlockPassword } : {}),
      });

      const verification = await verifyProof(decryptedPayload, openedSeed.header.manifestHash);
      setPayload(decryptedPayload);
      setSealVerified(sealValid);
      setAuthorityVerified(authorityOk);
      setProofState({ valid: verification.valid && sealValid && authorityOk, errors: verification.errors });
      setBehaviorRuntime(null);
      setUnlockPassword('');

      toast({
        title: 'Seed unlocked',
        description: 'The dossier decrypted successfully after MapleVault authority verification.',
      });
    } catch {
      resetUnlockedState();
      setUnlockErrorMessage(UNLOCK_FAILURE_MESSAGE);
      toast({ title: 'Unlock failed', description: UNLOCK_FAILURE_MESSAGE, variant: 'destructive' });
    } finally {
      setUnlocking(false);
    }
  };

  const handleRunBehavior = async () => {
    if (!payload?.behavior) {
      return;
    }

    try {
      const runtime = await instantiateWasmBehavior(payload.behavior);
      const exportNames = Object.keys(runtime.exports);
      let result: string | undefined;

      if (typeof runtime.exports.run === 'function') {
        result = String(runtime.exports.run());
      } else if (typeof runtime.exports.add === 'function') {
        result = String(runtime.exports.add(1, 2));
      }

      setBehaviorRuntime({ exports: exportNames, result, logs: runtime.logs });
      toast({ title: 'Behavior executed', description: result ? `WASM returned ${result}.` : 'WASM exports are now available for inspection.' });
    } catch (error: any) {
      toast({ title: 'Behavior failed', description: error?.message || 'The WASM behavior could not be instantiated.', variant: 'destructive' });
    }
  };

  const headerStatus = dossierHeader ? [
    { label: 'Encrypted', value: dossierHeader.envelope.algorithm },
    { label: 'Key Wrap', value: dossierHeader.envelope.keyWrap },
    { label: 'Seed ID', value: dossierHeader.seedId.slice(0, 18) },
  ] : [];

  const trustStatus = dossierHeader ? [
    {
      label: 'Encrypted',
      value: dossierHeader.envelope.algorithm,
      verified: true,
    },
    {
      label: 'Password Protected',
      value: isPasswordProtectedSeed ? 'Enabled' : 'Not enabled',
      verified: isPasswordProtectedSeed,
    },
    {
      label: 'Authority Verified',
      value: authorityVerified ? 'Verified online' : 'Pending unlock',
      verified: authorityVerified,
    },
    {
      label: 'Seal Verified',
      value: sealVerified ? 'Seal valid' : 'Pending unlock',
      verified: sealVerified,
    },
    {
      label: 'Proof Valid',
      value: proofState ? (proofState.valid ? 'Proof chain valid' : 'Proof mismatch') : 'Pending unlock',
      verified: Boolean(proofState?.valid),
    },
  ] : [];

  const lockedDossierTitle = filePath?.split('\\').pop() || 'Locked Maple Seed';
  const dossierTitle = payload?.manifest.title || lockedDossierTitle;
  const dossierSummary = payload?.manifest.summary || 'This .mseed container is locked. Unlock it through MapleOS to reveal the dossier contents.';
  const overviewCreatedAt = payload?.manifest.createdAt || dossierHeader?.createdAt;

  return (
    <div className="h-full w-full bg-background text-foreground">
      <ScrollArea className="h-full">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 p-6">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Archive className="h-5 w-5 text-primary" />
                    <CardTitle>MapleSeed Vault</CardTitle>
                    <Badge variant="secondary">.mseed</Badge>
                  </div>
                  <CardDescription>
                    Create encrypted Maple seeds, optionally add a password gate, and keep final unlock decisions under MapleVault authority control.
                  </CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={onImportSeedClick}>
                    <Upload className="mr-2 h-4 w-4" />
                    Import .mseed
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('dossier')} disabled={!openedSeed && !builtHeader}>
                    <BookOpen className="mr-2 h-4 w-4" />
                    Open Dossier
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'create' | 'dossier')} className="space-y-4">
            <TabsList>
              <TabsTrigger value="create">Create Seed</TabsTrigger>
              <TabsTrigger value="dossier">Dossier</TabsTrigger>
            </TabsList>

            <TabsContent value="create" className="space-y-4">
              <div className="grid gap-4 lg:grid-cols-[1.35fr_0.95fr]">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <PackagePlus className="h-4 w-4 text-primary" />
                      Seed Builder
                    </CardTitle>
                    <CardDescription>
                      Bundle text, images, vectors, blueprints, behavior, and proof into a single encrypted binary.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="seed-title">Title</Label>
                        <Input id="seed-title" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Maple Seed title" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="seed-author">Author</Label>
                        <Input id="seed-author" value={author} onChange={(event) => setAuthor(event.target.value)} placeholder="MapleOS author" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="seed-summary">Summary</Label>
                      <Textarea id="seed-summary" value={summary} onChange={(event) => setSummary(event.target.value)} className="min-h-24" placeholder="Explain what this dossier contains and why it matters." />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="seed-tags">Tags</Label>
                      <Input id="seed-tags" value={tags} onChange={(event) => setTags(event.target.value)} placeholder="comma,separated,tags" />
                    </div>

                    <Separator />

                    <div className="space-y-4 rounded-lg border p-4">
                      <div className="space-y-1">
                        <Label>Protection mode</Label>
                        <p className="text-sm text-muted-foreground">
                          Choose standard authority protection or add a password gate on top of the existing MapleVault authority unlock flow.
                        </p>
                      </div>
                      <RadioGroup value={protectionMode} onValueChange={(value) => setProtectionMode(value as SeedProtectionMode)} className="grid gap-3 md:grid-cols-2">
                        <label className="flex cursor-pointer items-start gap-3 rounded-lg border p-4">
                          <RadioGroupItem value="standard" id="seed-protection-standard" className="mt-1" />
                          <div className="space-y-1">
                            <div className="font-medium">Standard authority-protected seed</div>
                            <p className="text-sm text-muted-foreground">
                              MapleVault authority unwraps and decrypts the seed without a password layer.
                            </p>
                          </div>
                        </label>
                        <label className="flex cursor-pointer items-start gap-3 rounded-lg border p-4">
                          <RadioGroupItem value="password" id="seed-protection-password" className="mt-1" />
                          <div className="space-y-1">
                            <div className="font-medium">Password-protected authority seed</div>
                            <p className="text-sm text-muted-foreground">
                              Recipients need both the password and MapleVault authority access to unlock.
                            </p>
                          </div>
                        </label>
                      </RadioGroup>

                      {isPasswordBuild ? (
                        <div className="space-y-4 rounded-lg border bg-muted/30 p-4">
                          <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                              <Label htmlFor="seed-password">Password</Label>
                              <div className="flex gap-2">
                                <Input
                                  id="seed-password"
                                  type={showBuildPassword ? 'text' : 'password'}
                                  value={buildPassword}
                                  onChange={(event) => setBuildPassword(event.target.value)}
                                  placeholder="Set a strong password"
                                />
                                <Button type="button" variant="outline" size="icon" onClick={() => setShowBuildPassword((value) => !value)}>
                                  {showBuildPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="seed-password-confirm">Confirm password</Label>
                              <Input
                                id="seed-password-confirm"
                                type={showBuildPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(event) => setConfirmPassword(event.target.value)}
                                placeholder="Re-enter the password"
                              />
                            </div>
                          </div>
                          <p className={`text-sm ${passwordStrength.tone}`}>{passwordStrength.label}</p>
                          <div className="space-y-1 text-sm text-muted-foreground">
                            <p>Recipients will need this password and MapleVault authority access to open the seed.</p>
                            <p>Share the password separately from the file.</p>
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="seed-runbook">Runbook / notes</Label>
                      <Textarea id="seed-runbook" value={runbookText} onChange={(event) => setRunbookText(event.target.value)} className="min-h-40 font-mono text-sm" placeholder="Write the text or markdown blocks that belong in this seed." />
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="seed-vector">Vector block (JSON array)</Label>
                        <Textarea id="seed-vector" value={vectorJson} onChange={(event) => setVectorJson(event.target.value)} className="min-h-28 font-mono text-sm" placeholder="[0.12, -0.08, 0.91]" />
                      </div>
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label>Image block</Label>
                          <div className="flex flex-wrap gap-2">
                            <Button type="button" variant="outline" onClick={onImageUploadClick}>
                              <ImageIcon className="mr-2 h-4 w-4" />
                              Add image
                            </Button>
                            {imageDataUrl ? <Badge variant="secondary">{imageName}</Badge> : <Badge variant="outline">No image</Badge>}
                          </div>
                        </div>
                        {imageDataUrl ? (
                          <img src={imageDataUrl} alt="Seed block preview" className="max-h-44 rounded-lg border object-cover" />
                        ) : (
                          <div className="flex h-44 items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
                            Add an image to package screenshots, scans, or evidence.
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <Label>Blueprint source</Label>
                          <p className="text-sm text-muted-foreground">Pull the latest Blueprint Studio draft into the seed as structured dossier data.</p>
                        </div>
                        <Button type="button" variant="outline" onClick={loadLatestBlueprintDraft}>
                          <Sparkles className="mr-2 h-4 w-4" />
                          Capture Blueprint Draft
                        </Button>
                      </div>
                      {blueprintSummary ? (
                        <div className="rounded-lg border bg-muted/40 p-3 text-sm">
                          <div className="font-medium">{blueprintSummary.name}</div>
                          <div className="mt-2 grid gap-2 sm:grid-cols-3">
                            <div>Nodes: <span className="font-medium">{blueprintSummary.nodeCount}</span></div>
                            <div>Edges: <span className="font-medium">{blueprintSummary.edgeCount}</span></div>
                            <div>Types: <span className="font-medium">{(blueprintSummary.nodeTypes || []).join(', ') || 'n/a'}</span></div>
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
                          No Blueprint Studio draft loaded.
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <Label htmlFor="seed-behavior">Behavior (WAT → WASM)</Label>
                        <Badge variant="outline">WASM runtime</Badge>
                      </div>
                      <Textarea id="seed-behavior" value={watSource} onChange={(event) => setWatSource(event.target.value)} className="min-h-44 font-mono text-sm" placeholder="Paste WAT source for the behavior bundle." />
                    </div>

                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button onClick={handleBuildSeed} disabled={building}>
                        {building ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                        Build .mseed
                      </Button>
                      <Button variant="outline" onClick={handleSaveToMapleOS} disabled={!builtBytes}>
                        <Save className="mr-2 h-4 w-4" />
                        Save to MapleOS
                      </Button>
                      <Button variant="outline" onClick={handleDownloadSeed} disabled={!builtBytes}>
                        <Download className="mr-2 h-4 w-4" />
                        Download .mseed
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <div className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <KeyRound className="h-4 w-4 text-primary" />
                        Security Envelope
                      </CardTitle>
                      <CardDescription>
                        The seed payload is encrypted with AES-256-GCM, wrapped for authority with RSA-OAEP-256, and can optionally require a password before the authority unlock step.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4 text-sm">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="rounded-lg border p-3">
                          <div className="mb-1 flex items-center gap-2 font-medium"><Lock className="h-4 w-4 text-primary" /> Payload</div>
                          <p className="text-muted-foreground">Encrypted locally before the file is written or downloaded.</p>
                        </div>
                        <div className="rounded-lg border p-3">
                          <div className="mb-1 flex items-center gap-2 font-medium"><Network className="h-4 w-4 text-primary" /> Unlock</div>
                          <p className="text-muted-foreground">MapleVault authority performs the final unlock decision and decryption step.</p>
                        </div>
                      </div>

                      <div className="rounded-lg border bg-muted/40 p-4">
                        <div className="mb-2 font-medium">Ready path inside MapleOS</div>
                        <Input value={savePath} onChange={(event) => setSavePath(event.target.value)} />
                      </div>

                      {builtHeader ? (
                        <div className="space-y-3 rounded-lg border p-4">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="font-medium">Built seed header</div>
                            <div className="flex flex-wrap gap-2">
                              <Badge>{Math.round((builtBytes?.length || 0) / 1024)} KB</Badge>
                              {seedHasPasswordProtection(builtHeader) ? <Badge variant="secondary">Password Protected</Badge> : null}
                              <Badge variant="outline">Authority Unlock Required</Badge>
                            </div>
                          </div>
                          <div className="grid gap-2 text-xs text-muted-foreground">
                            {headerStatus.map((item) => (
                              <div key={item.label} className="flex items-center justify-between gap-3 rounded bg-muted/40 px-2 py-1">
                                <span>{item.label}</span>
                                <span className="font-mono text-foreground">{item.value}</span>
                              </div>
                            ))}
                          </div>
                          {seedHasPasswordProtection(builtHeader) ? (
                            <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
                              <div className="mb-2 font-medium text-foreground">Share checklist</div>
                              <ul className="list-disc space-y-1 pl-5">
                                <li>Send the <span className="font-medium text-foreground">.mseed</span> file.</li>
                                <li>Send the password separately.</li>
                                <li>MapleVault cannot recover forgotten passwords unless a recovery feature is added later.</li>
                              </ul>
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <div className="rounded-lg border border-dashed p-4 text-muted-foreground">
                          Build a seed to preview the encrypted header and produce a downloadable binary.
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <ShieldCheck className="h-4 w-4 text-primary" />
                        Generate Authority Secrets
                      </CardTitle>
                      <CardDescription>
                        Generate the three Supabase secret values you need for the MapleOS Key Authority, then paste them into Edge Function secrets.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <Button onClick={handleGenerateAuthoritySecrets} disabled={generatingSecrets}>
                        {generatingSecrets ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                        Generate secrets now
                      </Button>

                      {authoritySecrets ? (
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <div className="flex items-center justify-between gap-2">
                              <Label>MAPLEOS_MSEED_PUBLIC_KEY_JWK</Label>
                              <Button type="button" size="sm" variant="outline" onClick={() => handleCopySecret('Public JWK', authoritySecrets.publicJwk)}>
                                <Copy className="mr-2 h-4 w-4" />
                                Copy
                              </Button>
                            </div>
                            <Textarea readOnly value={authoritySecrets.publicJwk} className="min-h-40 font-mono text-xs" />
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center justify-between gap-2">
                              <Label>MAPLEOS_MSEED_PRIVATE_KEY_JWK</Label>
                              <Button type="button" size="sm" variant="outline" onClick={() => handleCopySecret('Private JWK', authoritySecrets.privateJwk)}>
                                <Copy className="mr-2 h-4 w-4" />
                                Copy
                              </Button>
                            </div>
                            <Textarea readOnly value={authoritySecrets.privateJwk} className="min-h-56 font-mono text-xs" />
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center justify-between gap-2">
                              <Label>MAPLEOS_MSEED_SEAL_SECRET</Label>
                              <Button type="button" size="sm" variant="outline" onClick={() => handleCopySecret('Seal secret', authoritySecrets.sealSecret)}>
                                <Copy className="mr-2 h-4 w-4" />
                                Copy
                              </Button>
                            </div>
                            <Textarea readOnly value={authoritySecrets.sealSecret} className="min-h-24 font-mono text-xs" />
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                          Click <span className="font-medium text-foreground">Generate secrets now</span> to create a fresh RSA-OAEP keypair and HMAC seal secret in your browser.
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="dossier" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <FileArchive className="h-5 w-5 text-primary" />
                        <CardTitle>{dossierTitle}</CardTitle>
                        {payload ? <Badge variant="secondary">Unlocked</Badge> : <Badge variant="outline">Locked</Badge>}
                        {isPasswordProtectedSeed ? <Badge variant="secondary">Password Protected</Badge> : null}
                        {dossierHeader ? <Badge variant="outline">Authority Unlock Required</Badge> : null}
                      </div>
                      <CardDescription>
                        {dossierSummary}
                      </CardDescription>
                    </div>
                    {!payload && dossierHeader ? (
                      <div className="w-full max-w-md space-y-3 rounded-lg border bg-muted/30 p-4">
                        <div>
                          <div className="font-medium">Unlock panel</div>
                          <p className="text-sm text-muted-foreground">
                            {isPasswordProtectedSeed
                              ? 'This seed is password protected. MapleVault authority still performs the final unlock after password validation.'
                              : 'Unlock through MapleVault authority to decrypt and inspect the dossier.'}
                          </p>
                        </div>
                        {isPasswordProtectedSeed ? (
                          <div className="space-y-2">
                            <Label htmlFor="seed-unlock-password">Password</Label>
                            <div className="flex gap-2">
                              <Input
                                id="seed-unlock-password"
                                type={showUnlockPassword ? 'text' : 'password'}
                                value={unlockPassword}
                                onChange={(event) => setUnlockPassword(event.target.value)}
                                placeholder="Enter seed password"
                              />
                              <Button type="button" variant="outline" size="icon" onClick={() => setShowUnlockPassword((value) => !value)}>
                                {showUnlockPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </Button>
                            </div>
                          </div>
                        ) : null}
                        {unlockErrorMessage ? (
                          <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                            {unlockErrorMessage}
                          </div>
                        ) : null}
                        <Button onClick={handleUnlockSeed} disabled={!openedSeed || unlocking}>
                          {unlocking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Unlock className="mr-2 h-4 w-4" />}
                          Unlock in MapleOS
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </CardHeader>
                <CardContent>
                  {dossierHeader ? (
                    <div className="grid gap-3 md:grid-cols-3">
                      {headerStatus.map((item) => (
                        <div key={item.label} className="rounded-lg border p-3 text-sm">
                          <div className="text-muted-foreground">{item.label}</div>
                          <div className="mt-1 font-medium break-all">{item.value}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed p-6 text-center text-muted-foreground">
                      Import a .mseed file, save one into MapleOS, or build one from the Create Seed tab.
                    </div>
                  )}
                </CardContent>
              </Card>

              {dossierHeader && (
                <Tabs defaultValue="overview" className="space-y-4">
                  <TabsList>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="data">Data</TabsTrigger>
                    <TabsTrigger value="behavior">Behavior</TabsTrigger>
                    <TabsTrigger value="proof">Proof</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Dossier overview</CardTitle>
                        <CardDescription>The dossier header is visible before unlock; full content appears after the Key Authority decrypts it.</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                          <div className="rounded-lg border p-3 text-sm">
                            <div className="text-muted-foreground">Author</div>
                            <div className="mt-1 font-medium">{payload?.manifest.author || 'Encrypted until unlock'}</div>
                          </div>
                          <div className="rounded-lg border p-3 text-sm">
                            <div className="text-muted-foreground">Created</div>
                            <div className="mt-1 font-medium">{overviewCreatedAt ? new Date(overviewCreatedAt).toLocaleString() : 'Unavailable'}</div>
                          </div>
                          <div className="rounded-lg border p-3 text-sm">
                            <div className="text-muted-foreground">Blocks</div>
                            <div className="mt-1 font-medium">{payload ? payload.manifest.blockCount : 'Encrypted until unlock'}</div>
                          </div>
                          <div className="rounded-lg border p-3 text-sm">
                            <div className="text-muted-foreground">Behavior</div>
                            <div className="mt-1 font-medium">{payload ? (payload.manifest.hasBehavior ? 'Included' : 'None') : 'Encrypted until unlock'}</div>
                          </div>
                        </div>

                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                          {trustStatus.map((status) => (
                            <div key={status.label} className="rounded-lg border p-3 text-sm">
                              <div className="text-muted-foreground">{status.label}</div>
                              <div className="mt-2 flex items-center gap-2 font-medium">
                                {status.verified ? <ShieldCheck className="h-4 w-4 text-green-600 dark:text-green-400" /> : <Lock className="h-4 w-4 text-muted-foreground" />}
                                <span>{status.value}</span>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {payload?.manifest.tags.map((tag) => (
                            <Badge key={tag} variant="secondary">{tag}</Badge>
                          ))}
                        </div>

                        {payload ? (
                          <div className="rounded-lg border bg-muted/40 p-4 text-sm text-muted-foreground">
                            This dossier is unlocked and ready to browse in the Data, Behavior, and Proof tabs.
                          </div>
                        ) : (
                          <div className="rounded-lg border bg-muted/40 p-4 text-sm text-muted-foreground">
                            The payload remains encrypted until MapleVault authority unlocks it. Password-protected seeds also require the recipient password before authority decryption.
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="data">
                    <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">Knowledge blocks</CardTitle>
                          <CardDescription>Text, images, blueprint graphs, and vectors packaged into this seed.</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {!payload ? (
                            <div className="rounded-lg border border-dashed p-6 text-center text-muted-foreground">
                              Unlock the seed to view its bundled data blocks.
                            </div>
                          ) : (
                            <>
                              {textBlocks.map((block) => (
                                <div key={block.id} className="rounded-lg border p-4">
                                  <div className="mb-2 flex items-center justify-between gap-3">
                                    <div className="font-medium">{block.name}</div>
                                    <Badge variant="outline">text</Badge>
                                  </div>
                                  <pre className="whitespace-pre-wrap font-sans text-sm text-muted-foreground">{block.text}</pre>
                                </div>
                              ))}

                              {jsonBlocks.map((block) => (
                                <div key={block.id} className="rounded-lg border p-4">
                                  <div className="mb-2 flex items-center justify-between gap-3">
                                    <div className="font-medium">{block.name}</div>
                                    <Badge variant="outline">{block.kind}</Badge>
                                  </div>
                                  <pre className="overflow-x-auto rounded bg-muted/40 p-3 text-xs">{JSON.stringify(block.value, null, 2)}</pre>
                                </div>
                              ))}
                            </>
                          )}
                        </CardContent>
                      </Card>

                      <div className="space-y-4">
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-base"><ImageIcon className="h-4 w-4 text-primary" /> Images</CardTitle>
                          </CardHeader>
                          <CardContent>
                            {!payload || imageBlocks.length === 0 ? (
                              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                                {payload ? 'No image blocks were packaged into this seed.' : 'Unlock the seed to view any packaged images.'}
                              </div>
                            ) : (
                              <div className="grid gap-3">
                                {imageBlocks.map((block) => (
                                  <div key={block.id} className="space-y-2 rounded-lg border p-3">
                                    <div className="text-sm font-medium">{block.name}</div>
                                    <img src={block.dataUrl} alt={block.name} className="max-h-72 w-full rounded object-cover" />
                                  </div>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-base"><Waypoints className="h-4 w-4 text-primary" /> Vectors</CardTitle>
                          </CardHeader>
                          <CardContent>
                            {!payload || vectorBlocks.length === 0 ? (
                              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                                {payload ? 'No vector blocks were packaged into this seed.' : 'Unlock the seed to inspect packaged vectors.'}
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {vectorBlocks.map((block) => (
                                  <div key={block.id} className="rounded-lg border p-3 text-sm">
                                    <div className="mb-2 flex items-center justify-between">
                                      <div className="font-medium">{block.name}</div>
                                      <Badge variant="outline">{block.dimensions} dims</Badge>
                                    </div>
                                    <pre className="overflow-x-auto rounded bg-muted/40 p-3 text-xs">{JSON.stringify(block.values, null, 2)}</pre>
                                  </div>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="behavior">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base"><Binary className="h-4 w-4 text-primary" /> WASM behavior</CardTitle>
                        <CardDescription>Seed behavior is executed inside MapleOS&apos;s WebAssembly runtime surface.</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {!payload?.behavior ? (
                          <div className="rounded-lg border border-dashed p-6 text-center text-muted-foreground">
                            {payload ? 'This seed does not include a WASM behavior block.' : 'Unlock the seed to inspect and execute the bundled behavior.'}
                          </div>
                        ) : (
                          <>
                            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                              <div className="rounded-lg border p-3 text-sm">
                                <div className="text-muted-foreground">Module</div>
                                <div className="mt-1 font-medium">{payload.behavior.name}</div>
                              </div>
                              <div className="rounded-lg border p-3 text-sm">
                                <div className="text-muted-foreground">Source</div>
                                <div className="mt-1 font-medium uppercase">{payload.behavior.sourceFormat}</div>
                              </div>
                              <div className="rounded-lg border p-3 text-sm">
                                <div className="text-muted-foreground">Size</div>
                                <div className="mt-1 font-medium">{payload.behavior.byteLength} bytes</div>
                              </div>
                              <div className="rounded-lg border p-3 text-sm">
                                <div className="text-muted-foreground">CID</div>
                                <div className="mt-1 font-medium break-all">{payload.behavior.cid.slice(0, 18)}…</div>
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                              <Button onClick={handleRunBehavior}>
                                <Rocket className="mr-2 h-4 w-4" />
                                Execute behavior
                              </Button>
                            </div>

                            <div className="grid gap-4 lg:grid-cols-2">
                              <div className="rounded-lg border p-4">
                                <div className="mb-2 font-medium">Exports</div>
                                {behaviorRuntime ? (
                                  <div className="flex flex-wrap gap-2">
                                    {behaviorRuntime.exports.map((entry) => (
                                      <Badge key={entry} variant="secondary">{entry}</Badge>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-sm text-muted-foreground">Execute the behavior to inspect WASM exports.</div>
                                )}
                              </div>
                              <div className="rounded-lg border p-4">
                                <div className="mb-2 font-medium">Last result</div>
                                <div className="text-sm text-muted-foreground">{behaviorRuntime?.result || 'No execution result yet.'}</div>
                              </div>
                            </div>

                            {payload.behavior.sourceText ? (
                              <div className="rounded-lg border p-4">
                                <div className="mb-2 font-medium">Source</div>
                                <pre className="overflow-x-auto rounded bg-muted/40 p-3 text-xs">{payload.behavior.sourceText}</pre>
                              </div>
                            ) : null}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="proof">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base"><BadgeCheck className="h-4 w-4 text-primary" /> Proof and authority</CardTitle>
                        <CardDescription>Every block is content-addressed, chained, optionally password-gated, and protected by a MapleOS Key Authority seal.</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {!dossierHeader ? null : (
                          <div className="grid gap-4 md:grid-cols-3">
                            <div className="rounded-lg border p-3 text-sm">
                              <div className="text-muted-foreground">Seal</div>
                              <div className="mt-1 flex items-center gap-2 font-medium">
                                {sealVerified ? <ShieldCheck className="h-4 w-4 text-green-600" /> : <Lock className="h-4 w-4 text-muted-foreground" />}
                                {sealVerified ? 'Verified online' : 'Pending verification'}
                              </div>
                            </div>
                            <div className="rounded-lg border p-3 text-sm">
                              <div className="text-muted-foreground">Manifest hash</div>
                              <div className="mt-1 break-all font-medium">{dossierHeader.manifestHash}</div>
                            </div>
                            <div className="rounded-lg border p-3 text-sm">
                              <div className="text-muted-foreground">Seal key ID</div>
                              <div className="mt-1 break-all font-medium">{dossierHeader.seal.keyId}</div>
                            </div>
                          </div>
                        )}

                        {proofState ? (
                          <div className={`rounded-lg border p-4 text-sm ${proofState.valid ? 'border-green-500/40 bg-green-500/5' : 'border-destructive/40 bg-destructive/5'}`}>
                            <div className="mb-2 font-medium">Local integrity check</div>
                            {proofState.valid ? (
                              <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                                <BadgeCheck className="h-4 w-4" />
                                Proof chain and manifest hash are consistent.
                              </div>
                            ) : (
                              <div className="space-y-2 text-destructive">
                                {proofState.errors.map((error) => (
                                  <div key={error}>{error}</div>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="rounded-lg border border-dashed p-6 text-center text-muted-foreground">
                            Unlock the seed to verify the proof chain and authority seal.
                          </div>
                        )}

                        {payload ? (
                          <div className="rounded-lg border p-4">
                            <div className="mb-3 font-medium">Proof chain</div>
                            <div className="space-y-2">
                              {payload.proof.chain.map((entry) => (
                                <div key={entry.hash} className="rounded-md bg-muted/40 p-3 text-xs">
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <span className="font-medium">#{entry.seq + 1} · {entry.kind}</span>
                                    <Badge variant="outline">{entry.blockId}</Badge>
                                  </div>
                                  <div className="mt-2 break-all text-muted-foreground">CID: {entry.cid}</div>
                                  <div className="mt-1 break-all text-muted-foreground">Prev: {entry.prev}</div>
                                  <div className="mt-1 break-all text-muted-foreground">Hash: {entry.hash}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              )}
            </TabsContent>
          </Tabs>

          <input ref={importInputRef} type="file" accept=".mseed" className="hidden" onChange={handleImportSeedFile} />
          <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelected} />
        </div>
      </ScrollArea>
    </div>
  );
};

export default MapleSeedVault;
